import { Margin, TextLink } from '@ovotech/nebula';
import { AnalyticsPage } from '@ovotech/ui-tools';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, Redirect } from 'react-router-dom-v5';
import { footnote100PercentGreenEnergy } from '../components/Footnotes/GreenerEnergyFootnotes';
import GreenerEnergyHomeHeader from '../components/GreenerEnergyHomeHeader/GreenerEnergyHomeHeader';
import {
  FeaturesWrapper,
  TermsHeader,
  TermsContent,
  TermsContainer,
} from './GreenerEnergySignup.styled';
import { CarbonNeutralCard } from './components/CarbonNeutralCard';
import { FooterLogo } from './components/FooterLogo';
import { GreenerEnergyHeader } from './components/GreenerEnergyHeader';
import {
  LoadError,
  Loading,
  FormError,
} from './components/GreenerEnergySignupComponents';
import { RenewablePower } from './components/RenewablePower';
import { SignupBanner } from './components/SignupBanner';
import { WhatIsIncluded } from './components/WhatIsIncluded';
import { useGreenerEnergySignup } from './hooks/useGreenerEnergySignup';

import { StyledFootnoteHeading } from '@/src/components/Footnote/Footnote.styled';
import { GREENER_ENERGY_SIGNUP } from '@/src/constants/analytics-subjects';

import {
  ROUTE_GREENER_ENERGY_SIGNUP_SUCCESS,
  ROUTE_GREENER_ENERGY,
} from '@/src/constants/routes';

import { getProductCatalogueStart } from '@/src/redux/ducks/products/products';
import { OVO_ENERGY_TERMS } from '@/src/constants/links';
import { getQueryParamValue } from '@/src/utils/getQueryParamValue';

export const GreenerEnergySignup = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const {
    isFetching,
    hasStandaloneGreenerEnergyAddOn,
    activateProduct,
    greenerEnergy,
    activation,
  } = useGreenerEnergySignup();

  const promoCode = getQueryParamValue('promoCode');
  const [acceptPrompt, setAcceptPrompt] = useState<boolean>(false);

  useEffect(() => {
    // Redirect to Greener Energy
    if (hasStandaloneGreenerEnergyAddOn) {
      history.push(ROUTE_GREENER_ENERGY);
    } else {
      // If a user is not a Greener Energy member then fetch an up to date status for Greener Energy eligibility
      dispatch(getProductCatalogueStart());
    }
  }, [hasStandaloneGreenerEnergyAddOn, history, dispatch]);

  if (activation.success) {
    return <Redirect to={ROUTE_GREENER_ENERGY_SIGNUP_SUCCESS} />;
  }
  if (isFetching) {
    return <Loading />;
  }
  if (activation.error) {
    return <FormError />;
  }

  if (greenerEnergy?.eligibility?.reason === 'Conflict') {
    return (
      <AnalyticsPage name={GREENER_ENERGY_SIGNUP} properties={{ promoCode }}>
        <GreenerEnergyHomeHeader headingContent="Greener Energy is included with your tariff" />
      </AnalyticsPage>
    );
  }

  if (!greenerEnergy || greenerEnergy?.eligibility?.reason) {
    return <LoadError />;
  }

  return (
    <AnalyticsPage name={GREENER_ENERGY_SIGNUP} properties={{ promoCode }}>
      <GreenerEnergyHeader />
      <SignupBanner
        setAcceptPrompt={setAcceptPrompt}
        acceptPrompt={acceptPrompt}
        activateProduct={activateProduct}
        activation={activation}
        greenerEnergy={greenerEnergy}
      ></SignupBanner>
      <WhatIsIncluded></WhatIsIncluded>
      <FeaturesWrapper>
        <CarbonNeutralCard />
        <RenewablePower />
      </FeaturesWrapper>
      <Margin top={2}>
        <FooterLogo />
      </Margin>
      <StyledFootnoteHeading>More information</StyledFootnoteHeading>
      <TermsContainer>
        <ul>
          <li>
            <TermsHeader>{footnote100PercentGreenEnergy.heading}</TermsHeader>
            <TermsContent>{footnote100PercentGreenEnergy.body[0]}</TermsContent>
          </li>
        </ul>
        <Margin bottom={6}>
          <TextLink
            opensInNewWindow
            href={`${OVO_ENERGY_TERMS}/greener-energy`}
          >
            Greener Energy terms and conditions
          </TextLink>
        </Margin>
      </TermsContainer>
    </AnalyticsPage>
  );
};
